import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DeaktivierungsGrundAdmin, DeaktivierungsGrundAdminCmd } from '@app/shared/classes/deaktivierungs-grund';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'app-admin-stammdaten-deaktivierungsgrund-entry',
  templateUrl: './admin-stammdaten-deaktivierungsgrund-entry.component.html',
  imports: [
    NgClass,
    NgStyle,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class AdminStammdatenDeaktivierungsgrundEntryComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly deleted = output();
  readonly reloadEntries = output();

  protected readonly grundForm = signal(this.generateForm());
  protected readonly delChosen = signal(false);

  private grund: DeaktivierungsGrundAdmin | null = null;

  private generateForm(): UntypedFormGroup {
    return this.formBuilder.group({
      sortierung    : [this.grund?.sortierung    ?? '', [Validators.pattern('^[0-9]*$'), Validators.required]],
      bezeichnungDe : [this.grund?.bezeichnungDe ?? '', Validators.required],
      bezeichnungFr : [this.grund?.bezeichnungFr ?? '', Validators.required],
      bezeichnungIt : [this.grund?.bezeichnungIt ?? '', Validators.required],
      textDe        : [this.grund?.textDe        ?? '', Validators.required],
      textFr        : [this.grund?.textFr        ?? '', Validators.required],
      textIt        : [this.grund?.textIt        ?? '', Validators.required]
    });
  }

  @Input()
  set set(grund: DeaktivierungsGrundAdmin) {
    if (!grund) {
      throw new Error('DeaktivierungsGrund must exist');
    }
    this.grund = grund;
    this.reset();
  }

  protected submit() {
    if (this.grund) {
      const formValues = this.grundForm().value;
      const cmd = new DeaktivierungsGrundAdminCmd({
        id            : this.grund.id,
        sortierung    : formValues.sortierung,
        bezeichnungDe : formValues.bezeichnungDe,
        bezeichnungFr : formValues.bezeichnungFr,
        bezeichnungIt : formValues.bezeichnungIt,
        textDe        : formValues.textDe,
        textFr        : formValues.textFr,
        textIt        : formValues.textIt
      });
      this.backendService.editDeaktivierungsGrund(cmd)
        .subscribe({
          next: result => {
            this.grund = result;
            this.reloadEntries.emit();
          },
          error: console.error
        });
    }
  }

  protected reset() {
    this.grundForm.set(this.generateForm());
  }

  protected confirmDelete() {
    const grund = this.grund;
    if (grund) {
      this.delChosen.set(true);
      Swal.fire({
        text              : this.translateService.instant('admin.deaktivierungsgrund.delete.warning'),
        icon              : 'warning',
        buttonsStyling    : true,
        showCancelButton  : true,
        confirmButtonText : this.translateService.instant('admin.deaktivierungsgrund.delete.yes'),
        cancelButtonText  : this.translateService.instant('global.cancel')
      }).then((result) => {
        if (result.value) {
          this.delete(grund.id);
        } else {
          this.delChosen.set(false);
        }
      });
    }
  }

  private delete(grundId: number) {
    this.backendService.deleteDeaktivierungsGrund(grundId)
      .subscribe({
        next: () => {
          this.showAlert(true);
          this.deleted.emit();
        },
        error: error => {
          this.showAlert(false);
          console.error(error.message);
        }
      });
  }

  private showAlert(success: boolean) {
    const msgCode = success
      ? 'admin.deaktivierungsgrund.delete.success'
      : 'admin.deaktivierungsgrund.delete.error';

    Swal.fire({
      text              : this.translateService.instant(msgCode),
      icon              : success ? 'success' : 'error',
      buttonsStyling    : true,
      confirmButtonText : this.translateService.instant('global.ok')
    });
  }
}
